import classnames from 'classnames';
import { BasicNumberFormatterProps } from 'components/shared/NumberFormatter/NumberFormatter.model';
import { FC, KeyboardEvent, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { NumberFormatValues, SourceInfo } from 'react-number-format';

import { type NumericValueType, useApplyValueChange, useFocusedAndSelectAll, useFreezeValueDuringEdit } from './hooks';
import { MyNumericFormat } from './MyNumericFormat';
import { getSeparatorsFromLocale } from './NumberFormatter.helper';

export const NumberFormatter: FC<BasicNumberFormatterProps> = ({
  className,
  decimalPadding,
  e2e,
  id,
  incrementStep,
  isDisabled = false,
  isReadOnly = false,
  isEdited = false,
  isTextRight = false,
  lostInputValue,
  max,
  min,
  onBlur,
  onChange,
  onNullableChange,
  onFocus,
  onKeyDown,
  onKeyPress,
  onPaste,
  onIsEditingChange,
  placeholder,
  precision,
  suffix,
  tabIndex,
  value: rawValue,
  hasError,
  highlightColor,
  isLoading,
  autoComplete,
  isFocused = false,
  isSelectAll,
  accelerators,
  isInfo = false,
}) => {
  const intl = useIntl();
  const {
    handleBlur,
    handleFocus,
    currentValue: value,
    markDirty,
    markPristine,
  } = useFreezeValueDuringEdit({ onBlur, onFocus, value: rawValue, lostInputValue, onIsEditingChange });
  const separators = getSeparatorsFromLocale(intl.locale);
  const [internalValue, applyValueChange] = useApplyValueChange(value, markDirty);
  const inputRef = useFocusedAndSelectAll(isFocused, isSelectAll ?? false);

  const applyBoundaries = useCallback(
    (floatValue: number | undefined) => {
      if (min !== undefined && floatValue !== undefined && floatValue < Number(min)) {
        return Number(min);
      }
      if (max !== undefined && floatValue !== undefined && floatValue > Number(max)) {
        return Number(max);
      }
      return floatValue;
    },
    [min, max],
  );

  const handleValueChange = useCallback(
    ({ floatValue, value }: NumberFormatValues, sourceInfo: SourceInfo) => {
      if (sourceInfo.source === 'event') {
        markDirty();
      }
      if (onChange !== undefined && isReadOnly !== true) {
        onChange(floatValue);
      }
      if (onNullableChange !== undefined && isReadOnly !== true) {
        onNullableChange(value === '' ? null : floatValue);
      }
    },
    [onChange, onNullableChange, isReadOnly, markDirty],
  );

  const applyCalculation = useCallback(
    (old: NumericValueType, calculation: (v: number) => number, defaultValue: number) => {
      const newValue = applyBoundaries(calculation(old ?? defaultValue));
      applyValueChange(old, newValue);
    },
    [applyValueChange, applyBoundaries],
  );

  const handleOnKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>, inputValue: number | undefined) => {
      if (isReadOnly) {
        return;
      }

      if (e.key === 'Escape') {
        markPristine();
        applyValueChange(inputValue, rawValue);
      } else {
        const accelerator = accelerators?.find(a => a.key === e.key.toLowerCase());
        if (accelerator !== undefined) {
          applyCalculation(inputValue, accelerator.transform, 1);
        } else if (incrementStep !== undefined && e.key === 'ArrowDown') {
          applyCalculation(inputValue, v => v - incrementStep, 0);
        } else if (incrementStep !== undefined && e.key === 'ArrowUp') {
          applyCalculation(inputValue, v => v + incrementStep, 0);
        }
      }

      if (onKeyDown !== undefined) {
        onKeyDown(e);
      }
    },
    [onKeyDown, accelerators, incrementStep, applyValueChange, applyCalculation, markPristine, rawValue, isReadOnly],
  );

  return (
    <>
      {isEdited && (
        <div className="input-group-text">
          <i className="icon  icon-sm text-info ">fiber_manual_record</i>
        </div>
      )}
      <MyNumericFormat
        applyBoundaries={applyBoundaries}
        min={min}
        max={max}
        step={incrementStep}
        decimalScale={precision}
        decimalSeparator={separators.decimalSeparator}
        disabled={isDisabled || isLoading}
        readOnly={isReadOnly}
        fixedDecimalScale={decimalPadding}
        getInputRef={inputRef}
        id={id ?? e2e}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleOnKeyDown}
        onValueChange={handleValueChange}
        onKeyPress={onKeyPress}
        suffix={suffix}
        tabIndex={tabIndex}
        placeholder={placeholder}
        thousandSeparator={separators.thousandSeparator}
        allowNegative={true}
        value={internalValue ?? ''}
        className={classnames(
          'text-field-component',
          'form-control',
          className,
          highlightColor ? `border-bottom border-${highlightColor}` : '',
          {
            'is-warning': hasError,
            'border border-info': isLoading,
            error: hasError,
            'text-info': isEdited,
            'fw-medium': isEdited,
            'text-end me-3': isTextRight,
            'is-edited': isInfo,
          },
        )}
        data-e2e={e2e}
        data-testid={e2e}
        data-e2e-loading={isLoading ? 'true' : undefined}
        autoComplete={autoComplete ?? 'off'}
        onPaste={onPaste as any} // FIXME
      />
    </>
  );
};
